// localStorage-backed "have I looked at this case yet?" tracker. Drives
// the COD inbox NEW indicator + dimming for already-reviewed cases.
//
// One global key (no per-user scoping) — in practice a browser maps 1:1
// to an operator account, and a logged-out viewer's seen state is fine
// to share. If we ever need stricter separation we can wrap the helpers
// with a userId suffix without touching the call sites.

const STORAGE_KEY = "care-os.seenCases";
// Bound to keep storage modest. LRU eviction by `seen_at` timestamp.
const MAX_ENTRIES = 1000;

type SeenMap = Record<string, number>; // caseId(string) -> epoch ms

function safeRead(): SeenMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed as SeenMap;
    return {};
  } catch {
    return {};
  }
}

function safeWrite(map: SeenMap): void {
  if (typeof window === "undefined") return;
  try {
    let target = map;
    const keys = Object.keys(map);
    if (keys.length > MAX_ENTRIES) {
      // Drop oldest until we fit. Sort by timestamp asc and slice.
      const ordered = keys.sort((a, b) => map[a] - map[b]);
      const keep = ordered.slice(ordered.length - MAX_ENTRIES);
      target = Object.fromEntries(keep.map(k => [k, map[k]])) as SeenMap;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(target));
  } catch {
    /* quota / disabled storage — accept silently */
  }
}

export function getSeenSet(): Set<string> {
  return new Set(Object.keys(safeRead()));
}

export function isSeen(caseId: string | number | null | undefined): boolean {
  if (caseId == null) return false;
  const map = safeRead();
  return Object.prototype.hasOwnProperty.call(map, String(caseId));
}

export function markSeen(caseId: string | number | null | undefined): void {
  if (caseId == null) return;
  const map = safeRead();
  map[String(caseId)] = Date.now();
  safeWrite(map);
}

// Best-effort cleanup: drop ids no longer present in the inbox snapshot.
// Prevents indefinite growth from closed/deleted cases. Call from the
// COD parent after each poll.
export function pruneSeen(keep: Set<string>): void {
  if (typeof window === "undefined") return;
  const map = safeRead();
  let changed = false;
  for (const k of Object.keys(map)) {
    if (!keep.has(k)) { delete map[k]; changed = true; }
  }
  if (changed) safeWrite(map);
}
